import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { useApp, CURRENCIES } from "@/context/AppContext";
import FixedHeader from "@/components/site/FixedHeader";
import Footer from "@/components/site/Footer";
import WhatsAppFab from "@/components/site/WhatsAppFab";
import OptimiseExplainer from "@/optimise/OptimiseExplainer";
import { PhoneCountryFields, isPhoneValidOrEmpty } from "@/optimise/PhoneCountryFields";
import {
  OrangeScoreReveal, CategoriesGrid, PrioritiesReveal, AnalysingLabels,
} from "@/optimise/AnimatedScanReveal";
import "@/optimise/phone-field.css";

const API = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_CURRENCIES = ["GBP", "USD", "EUR", "AED"];
const PAYPAL_CURRENCIES = ["GBP", "USD", "EUR"]; // AED unsupported for recurring

function scoreBand(score) {
  const s = Number(score || 0);
  if (s >= 80) return "good";
  if (s >= 50) return "needs_attention";
  return "poor";
}
function scoreCopy(score, hero) {
  // Phase 3A.1: SHORT status label for the score ring/hero.
  // V2 scans supply `hero.status`. Legacy V1 scans fall back to score bands.
  if (hero && hero.status) {
    return {
      EXCELLENT:        "LOOKING FRESH. 🍊",
      GOOD_FOUNDATIONS: "GOOD FOUNDATIONS.",
      NEEDS_ATTENTION:  "NEEDS ATTENTION.",
      URGENT:           "URGENT ATTENTION NEEDED.",
    }[hero.status] || "";
  }
  if (score >= 80) return "LOOKING FRESH. 🍊";
  if (score >= 60) return "A LITTLE MORE JUICE COULD HELP. 🍊";
  return "YOUR WEBSITE NEEDS A SQUEEZE. 🍊";
}
function bandColor(band) {
  if (band === "good") return "#0FA958";
  if (band === "needs_attention") return "#E8A03B";
  return "#C1350B";
}
function formatPlanPrice(minorUnits, currencyCode) {
  const cur = CURRENCIES.find((c) => c.code === currencyCode) || CURRENCIES[0];
  const value = (Number(minorUnits) || 0) / 100;
  const isWhole = Math.abs(value - Math.round(value)) < 0.005;
  const formatted = isWhole
    ? Math.round(value).toLocaleString("en-US")
    : value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cur.symbol}${formatted}`;
}
function planTierAccent(planKey) {
  if (planKey === "orange_optimise_large")  return { badge: "PRIORITY" };
  if (planKey === "orange_optimise_medium") return { badge: "RECOMMENDED" };
  return { badge: "STARTER" };
}

function CategoryCard() { return null; }
CategoryCard.displayName = "LegacyCategoryCard_unused";
// (Legacy stub retained to keep the module cleanly re-exportable; the live
// UI now uses the shared AnimatedScanReveal.CategoriesGrid component.)

function PlanCard({ plan, currency, cycle, isRecommended, onSubscribe, submitting, uiCurrencies }) {
  const price = plan?.prices?.[currency]?.[cycle];
  const currencyOk = !uiCurrencies || uiCurrencies.length === 0 || uiCurrencies.includes(currency);
  return (
    <div className={`relative rounded-3xl p-6 md:p-7 border-2 flex flex-col ${isRecommended ? "border-black bg-white shadow-[0_30px_60px_-30px_rgba(0,0,0,0.3)]" : "border-[#EAEAEA] bg-white"}`}
         data-testid={`optimise-plan-card-${plan.plan_key}`}>
      {isRecommended && (
        <span className="absolute -top-3 left-6 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.24em] bg-[#FF6A00] text-black">
          RECOMMENDED FOR YOU
        </span>
      )}
      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Orange Optimise</div>
      <h3 className="mt-1 font-black text-2xl tracking-[-0.02em]">Keep my website healthy</h3>
      <div className="mt-5 flex items-baseline gap-2">
        <div className="font-black text-4xl tracking-[-0.03em]" data-testid={`optimise-plan-price-${plan.plan_key}`}>
          {price ? formatPlanPrice(price, currency) : "—"}
        </div>
        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">{cycle === "monthly" ? "/month" : "/year"}</div>
      </div>
      {cycle === "annual" && price && plan?.prices?.[currency]?.monthly && (
        <div className="mt-1 text-[11px] tracking-[0.2em] uppercase text-[#8A8A8A]">
          vs {formatPlanPrice(plan.prices[currency].monthly * 12, currency)} billed monthly
        </div>
      )}
      <ul className="mt-6 space-y-2.5 flex-1">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex gap-2 text-[14px] text-black/85">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF6A00] shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => onSubscribe(plan)}
              disabled={submitting || !price || !currencyOk}
              data-testid={`optimise-plan-subscribe-${plan.plan_key}`}
              title={currencyOk ? undefined : `Recurring subscriptions in ${currency} aren't available yet — please switch currency.`}
              className={`mt-6 px-6 py-3.5 rounded-full font-black text-[12px] tracking-[0.2em] uppercase disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isRecommended ? "bg-black text-white hover:bg-[#1A1A1A]"
                              : "bg-white text-black border-2 border-black hover:bg-black hover:text-white"
              }`}>
        {submitting ? "Preparing…" : currencyOk ? "Keep my website healthy" : `Not available in ${currency}`}
      </button>
    </div>
  );
}

function RecommendedPlanSection({ result, onCheckout, checkoutState }) {
  const { currency: appCurrency, setCurrency } = useApp();
  const [cycle, setCycle] = useState("monthly");
  const [plansData, setPlansData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const complexity = result?.result?.complexity?.label || result?.complexity;
  const currency = SUPPORTED_CURRENCIES.includes(appCurrency) ? appCurrency : "GBP";

  useEffect(() => {
    let alive = true;
    setPlansData(null); setLoadErr("");
    const params = complexity ? { complexity } : {};
    axios.get(`${API}/api/optimise/plans`, { params })
      .then((r) => alive && setPlansData(r.data))
      .catch(() => alive && setLoadErr("We couldn't load plans right now."));
    return () => { alive = false; };
  }, [complexity]);

  if (loadErr) return <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 text-sm text-black/70" data-testid="optimise-plans-error">{loadErr}</div>;
  if (!plansData) return <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 text-sm text-black/50" data-testid="optimise-plans-loading">Loading plans…</div>;

  const plans = plansData.items || [];
  const recommendedKey = plansData.recommended_plan_key;

  return (
    <div className="space-y-6" data-testid="optimise-plans">
      <div className="bg-white border-2 border-black rounded-3xl p-6 md:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Keep my website healthy</div>
            <h2 className="mt-2 font-black text-3xl md:text-4xl tracking-[-0.02em]">Orange Optimise · continuous monitoring</h2>
            <p className="mt-2 text-[15px] text-black/70 max-w-xl">
              Orange Optimise continuously monitors your website, tracks changes and alerts you when something needs attention. Ongoing rescans, weakest-category tracking, change detection and issue alerts — cancel anytime.
            </p>
            <p className="mt-3 text-[12px] text-black/60 max-w-2xl leading-relaxed" data-testid="optimise-fix-disclosure">
              <strong className="text-black">Orange Fix access is included with Orange Optimise.</strong> Technical remediation work is quoted separately.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <div className="inline-flex items-center rounded-full bg-[#F6F5F2] p-1 h-9" data-testid="optimise-plans-cycle">
                {["monthly", "annual"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCycle(c)}
                    data-testid={`optimise-cycle-${c}`}
                    className={`inline-flex items-center h-7 px-4 rounded-full text-[11px] font-black tracking-[0.2em] uppercase transition-colors ${cycle === c ? "bg-black text-white" : "text-black/60 hover:text-black"}`}
                  >
                    {c === "monthly" ? "Monthly" : "Annual"}
                  </button>
                ))}
              </div>
              {(() => {
                // Server-authoritative savings pill — never invented in FE.
                const rec = plansData?.items?.find((p) => p.plan_key === recommendedKey) || plansData?.items?.[1];
                const s = rec?.annual_savings?.[currency];
                const months = s?.months_saved ? Math.floor(s.months_saved) : 0;
                if (months < 1) return null;
                return (
                  <span
                    data-testid="optimise-annual-save-badge"
                    className="inline-flex items-center h-9 px-3 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase"
                  >
                    SAVE {months} MONTH{months > 1 ? "S" : ""}
                  </span>
                );
              })()}
            </div>
            <div className="inline-flex items-center rounded-full bg-[#F6F5F2] p-1 h-9" data-testid="optimise-plans-currency">
              {SUPPORTED_CURRENCIES.map((c) => (
                <button key={c} type="button" onClick={() => setCurrency(c)} data-testid={`optimise-currency-${c}`}
                        className={`inline-flex items-center h-7 px-3 rounded-full text-[10px] font-black tracking-[0.2em] uppercase transition-colors ${currency === c ? "bg-black text-white" : "text-black/60 hover:text-black"}`}>
                  {c}
                </button>
              ))}
            </div>
            {currency === "AED" && (
              <div className="max-w-xs text-[11px] tracking-[0.14em] uppercase text-[#8A4A00]" data-testid="optimise-plans-aed-notice">
                AED subscriptions billed by card via Stripe.
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 max-w-3xl mx-auto"
             data-testid="optimise-pricing-grid">
          {/* FREE SCAN card (informational — the free scan is already
              accessible above; we surface it here so the pricing decision
              is a simple two-choice.) */}
          <div className="rounded-3xl p-6 md:p-7 border-2 border-[#EAEAEA] bg-white flex flex-col"
               data-testid="optimise-free-card">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Free website scan</div>
            <h3 className="mt-1 font-black text-2xl tracking-[-0.02em]">How healthy is my website?</h3>
            <div className="mt-5 flex items-baseline gap-2">
              <div className="font-black text-4xl tracking-[-0.03em]">£0</div>
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">one-off</div>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {["Orange Score", "Website health scan", "Health category breakdown", "Issues & recommendations"].map((f, i) => (
                <li key={i} className="flex gap-2 text-[14px] text-black/85">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF6A00] shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <a href="#optimise-scan" data-testid="optimise-free-scan-cta"
               className="mt-6 inline-flex items-center justify-center px-6 py-3.5 rounded-full font-black text-[12px] tracking-[0.2em] uppercase bg-white text-black border-2 border-black hover:bg-black hover:text-white transition-colors">
              Scan my website free
            </a>
          </div>
          {/* Paid Orange Optimise card — canonical single plan. */}
          {plans.map((p) => (
            <PlanCard key={p.plan_key} plan={p} currency={currency} cycle={cycle}
                      isRecommended={true}
                      uiCurrencies={checkoutState.availability?.recurring_supported_currencies_for_ui}
                      onSubscribe={(plan) => onCheckout({ plan, currency, cycle })}
                      submitting={checkoutState.submittingKey === p.plan_key} />
          ))}
        </div>
        {checkoutState.error && (
          <div className="mt-6 px-5 py-3 rounded-2xl bg-black text-white text-sm" data-testid="optimise-checkout-error">
            {checkoutState.error}
          </div>
        )}
      </div>
    </div>
  );
}

export function CustomerDetailsModal({ open, plan, currency, cycle, scan, onClose, onSubmit, submitting, error, availability, prefill }) {
  const [form, setForm] = useState({ full_name: "", business_name: "", email: "", phone: "", country: "", country_code: "", website_url: "" });

  useEffect(() => {
    if (!open) return;
    // Phase 3I.3N (Issue 6) — prefill from the continuation token first,
    // fall back to the anonymous scan URL if that's all we know.
    setForm((f) => ({
      full_name:     prefill?.full_name     ?? f.full_name,
      business_name: prefill?.business_name ?? f.business_name,
      email:         prefill?.email         ?? f.email,
      phone:         prefill?.phone         ?? f.phone,
      country:       prefill?.country       ?? f.country,
      country_code:  prefill?.country_code  ?? f.country_code,
      website_url:   prefill?.website_url   ?? scan?.submitted_url ?? f.website_url,
    }));
  }, [open, scan, prefill]);

  if (!open || !plan) return null;
  const price = plan?.prices?.[currency]?.[cycle];

  // Consistent input styling — white bg, near-black text, clear placeholder,
  // OrangeBySite orange focus ring, and an autofill fix so browsers don't
  // repaint the field dark-on-dark.
  const inputCls =
    "w-full px-4 py-3 rounded-xl border-2 border-[#EAEAEA] bg-white text-[#0A0A0A] placeholder:text-[#8A8A8A] text-[14px] focus:outline-none focus:border-[#FF6A00] focus:ring-4 focus:ring-[#FF6A00]/20 transition-colors [&:-webkit-autofill]:[-webkit-text-fill-color:#0A0A0A] [&:-webkit-autofill]:[transition:background-color_5000s_ease-in-out_0s] [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_white_inset]";
  const labelCls = "text-[10px] tracking-[0.24em] uppercase font-bold text-[#0A0A0A]/70 mb-1";

  // The provider label surfaced in the customer-facing copy is driven by
  // the currency → provider routing map returned by the availability endpoint.
  const routedProvider = (availability?.currency_routing || {})[currency] || "paypal";
  const providerLabel = routedProvider === "stripe" ? "Stripe" : "PayPal";
  const availabilityMsg =
    availability && !availability.available && routedProvider === "paypal"
      ? (availability.mode === "sandbox"
          ? "PayPal Sandbox credentials aren't fully set in this environment yet — we can't start a subscription right now."
          : "Checkout isn't configured yet in this environment.")
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-6"
         data-testid="optimise-checkout-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-lg p-6 md:p-8 space-y-5 max-h-[95vh] overflow-y-auto text-[#0A0A0A]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Confirm your details</div>
            <h3 className="mt-1 font-black text-2xl tracking-[-0.02em]">{plan.name}</h3>
            <div className="mt-1 text-[13px] text-black/70">
              {price ? formatPlanPrice(price, currency) : "—"} / {cycle === "annual" ? "year" : "month"} · billed via {providerLabel}{routedProvider === "paypal" && availability?.mode === "sandbox" ? " (Sandbox)" : ""}{routedProvider === "stripe" && availability?.stripe_mode === "test" ? " (Test)" : ""}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="optimise-checkout-modal-close"
                  className="text-[11px] font-black tracking-[0.2em] uppercase text-black/50 hover:text-black">Close</button>
        </div>

        {availabilityMsg && (
          <div className="px-4 py-3 rounded-xl bg-[#FFF3E6] border border-[#FCE1C7] text-[13px] text-[#8A4A00]" data-testid="checkout-modal-availability">
            {availabilityMsg}
          </div>
        )}

        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}>
          <label className="block">
            <div className={labelCls}>Your full name</div>
            <input required autoComplete="name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                   data-testid="checkout-full-name" placeholder="e.g. Nathan Rees" className={inputCls} />
          </label>
          <label className="block">
            <div className={labelCls}>Business name</div>
            <input required autoComplete="organization" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                   data-testid="checkout-business-name" placeholder="e.g. OrangeBySite Ltd" className={inputCls} />
          </label>
          <label className="block">
            <div className={labelCls}>Email</div>
            <input required type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="checkout-email" placeholder="you@yourbusiness.com" className={inputCls} />
          </label>
          <label className="block">
            <div className={labelCls}>Website</div>
            <input required autoComplete="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                   data-testid="checkout-website" placeholder="https://yourbusiness.com" className={inputCls} />
          </label>
          <PhoneCountryFields
            value={{ phone: form.phone, country: form.country, country_code: form.country_code }}
            onChange={(next) => setForm({ ...form, phone: next.phone, country: next.country, country_code: next.country_code })}
            disabled={submitting}
          />
          {error && <div className="px-4 py-3 rounded-xl bg-black text-white text-sm" data-testid="checkout-modal-error">{error}</div>}
          <button
            type="submit"
            disabled={
              submitting
              || (availability && !availability.available && routedProvider === "paypal")
              || !isPhoneValidOrEmpty(form.phone)
            }
            data-testid="checkout-modal-submit"
                  className="w-full mt-3 px-6 py-4 rounded-full bg-black text-white font-black text-[12px] tracking-[0.2em] uppercase disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? `Redirecting to ${providerLabel}…` : `Continue to ${providerLabel} · ${price ? formatPlanPrice(price, currency) : ""}`}
          </button>
          <div className="text-center text-[11px] text-black/50">
            You'll be redirected to {providerLabel}{routedProvider === "paypal" && availability?.mode === "sandbox" ? " Sandbox" : ""}{routedProvider === "stripe" && availability?.stripe_mode === "test" ? " (Test mode)" : ""} to authorise the subscription. You can cancel anytime.
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function CheckoutReturnCard({ mode, orderId }) {
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const isActive = order?.order?.status === "active";
  const wasPending = order?.order && !isActive;

  useEffect(() => {
    if (!orderId) return;
    let alive = true;
    let interval;
    const fetchOnce = () => axios.get(`${API}/api/optimise/checkout/${orderId}`)
      .then((r) => { if (alive) setOrder(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setTries((t) => t + 1); });
    fetchOnce();
    interval = setInterval(fetchOnce, 2000);
    return () => { alive = false; clearInterval(interval); };
  }, [orderId]);

  // Stop polling after ~30s or once active
  useEffect(() => {
    if (isActive || tries > 15) return;
  }, [isActive, tries]);

  if (mode === "cancelled") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="mt-10 bg-white border-2 border-black rounded-3xl p-8 md:p-12" data-testid="checkout-return-cancelled">
        <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Checkout cancelled</div>
        <h2 className="mt-3 font-black text-4xl tracking-[-0.02em]">You didn't complete checkout — that's OK. 🍊</h2>
        <p className="mt-3 text-[15px] text-black/70 max-w-xl">Nothing was charged and no monitoring has started. If you changed your mind, just run a scan again and pick a plan when you're ready.</p>
        <a href="/optimise" className="mt-6 inline-block px-8 py-4 rounded-full bg-black text-white font-black text-[13px] tracking-[0.2em] uppercase" data-testid="checkout-return-cancelled-cta">
          Try again
        </a>
      </motion.div>
    );
  }

  // Success flow (poll → pending → active)
  const domain = order?.order?.website_url || order?.order?.website?.normalized_domain || "your website";
  const planName = order?.plan?.name || "Orange Optimise";
  const price = order?.order ? formatPlanPrice(order.order.amount_minor, order.order.currency) : "";
  const cadence = order?.order?.cycle === "annual" ? "year" : "month";

  return (
    <div className="mt-10" data-testid="checkout-return-success">
      <AnimatePresence mode="wait" initial={false}>
        {isActive ? (
          <motion.div
            key="active"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white border-2 rounded-3xl p-8 md:p-12 relative overflow-hidden"
            style={{ borderColor: "#0FA958" }}
            data-testid="checkout-return-success-active"
          >
            <motion.div
              aria-hidden
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full"
              style={{ background: "radial-gradient(circle at center, rgba(15,169,88,0.18), transparent 65%)" }}
              initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
            />

            <div className="flex items-center gap-3">
              <motion.span
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.05, type: "spring", stiffness: 220, damping: 14 }}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-white"
                style={{ background: "#0FA958" }}
                data-testid="checkout-return-active-check"
              >
                <Check size={20} strokeWidth={3} />
              </motion.span>
              <div className="text-[11px] tracking-[0.32em] uppercase font-black" style={{ color: "#0FA958" }} data-testid="checkout-return-status-label">
                Subscription active
              </div>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12, duration: 0.45 }}
              className="mt-4 font-black text-4xl md:text-5xl tracking-[-0.02em]"
              data-testid="checkout-return-active-headline"
            >
              Your website is now being watched. 🍊
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22, duration: 0.5 }}
              className="mt-3 text-[15px] text-black/75 max-w-xl"
            >
              Orange Optimise is now monitoring <strong>{domain}</strong>. We'll watch its speed, mobile experience, technical setup and security config, and let you know when something important changes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32, duration: 0.5 }}
              className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-5 pt-5 border-t border-[#EAEAEA]"
              data-testid="checkout-return-active-details"
            >
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Plan</div>
                <div className="mt-1 font-black text-[15px]" data-testid="checkout-return-plan">{planName.replace("Orange Optimise · ", "Orange Optimise · ")}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Billing</div>
                <div className="mt-1 font-black text-[15px]" data-testid="checkout-return-billing">{price} / {cadence}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Status</div>
                <div className="mt-1 font-black text-[15px]" style={{ color: "#0FA958" }} data-testid="checkout-return-status">ACTIVE</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-black/50">Provider</div>
                <div className="mt-1 font-black text-[15px]" data-testid="checkout-return-provider">{(order?.order?.provider || "paypal") === "stripe" ? "Stripe" : "PayPal"}</div>
              </div>
            </motion.div>

            <motion.a
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.42, duration: 0.4 }}
              href="/optimise"
              data-testid="checkout-return-success-cta"
              className="mt-8 inline-block px-8 py-4 rounded-full bg-black text-white font-black text-[13px] tracking-[0.2em] uppercase hover:bg-[#1A1A1A]"
            >
              Scan another site
            </motion.a>
          </motion.div>
        ) : (
          <motion.div
            key="pending"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.4 }}
            className="bg-white border-2 border-black rounded-3xl p-8 md:p-12"
            data-testid="checkout-return-success-pending"
          >
            <div className="flex items-center gap-3">
              <motion.span
                aria-hidden
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}
                className="inline-block w-4 h-4 rounded-full border-2 border-[#FF6A00] border-t-transparent"
              />
              <div className="text-[11px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">
                {order?.order ? "Confirming subscription…" : "Fetching status…"}
              </div>
            </div>
            <h2 className="mt-4 font-black text-4xl md:text-5xl tracking-[-0.02em]">Almost there.</h2>
            <p className="mt-3 text-[15px] text-black/70 max-w-xl">
              We're confirming your subscription with the payment provider. This normally happens within a few seconds — this page will refresh automatically the moment we get the green light.
            </p>
            {tries > 16 && wasPending && (
              <div className="mt-5 px-4 py-3 rounded-2xl bg-[#FFF3E6] border border-[#FCE1C7] text-[13px] text-[#8A4A00]" data-testid="checkout-return-slow-notice">
                Still waiting on the payment provider — if this doesn't clear in a minute or two, just email us and we'll sort it (no charge if it doesn't land).
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function OptimisePage() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [checkoutState, setCheckoutState] = useState({ submittingKey: null, error: "" });
  const [modal, setModal] = useState({ open: false, plan: null, currency: "GBP", cycle: "monthly", error: "" });
  const [availability, setAvailability] = useState(null);
  // Phase 3I.3N (Issue 6) — continuation from a pitch email. When present,
  // we skip the free-scan entry, render the existing scan/result and
  // auto-open the checkout modal with pre-filled customer details.
  const [prefill, setPrefill] = useState(null);

  // Detect ?checkout=success|cancelled&order=...  and  ?continue=<token>
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const checkoutMode = params?.get("checkout");
  const checkoutOrderId = params?.get("order");
  const continueToken = params?.get("continue");
  // Phase 3I.4F — Orange Optimise Pitch Funnel V2. When the recipient
  // clicks the initial pitch CTA the URL carries a signed `pfu` token
  // that identifies the 48h follow-up row. We forward it on the scan
  // submission so the resulting scan is marked as
  // `origin=customer_pitch_initiated` and traced back to the pitch.
  const pfuToken = params?.get("pfu");
  const returnView = checkoutMode === "success" || checkoutMode === "cancelled";

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/optimise/checkout/availability`)
      .then((r) => alive && setAvailability(r.data))
      .catch(() => alive && setAvailability({ available: false, reason: "network" }));
    return () => { alive = false; };
  }, []);

  // Phase 3I.3N (Issue 6) — resolve continuation token to existing scan +
  // prefilled customer details. Fails silently to the public /optimise
  // page when the token is invalid or expired.
  useEffect(() => {
    if (!continueToken || returnView) return;
    let alive = true;
    axios.get(`${API}/api/optimise/continue/${continueToken}`)
      .then((r) => {
        if (!alive || !r.data?.valid) return;
        const s = r.data.scan || {};
        setResult({
          id: s.id,
          orange_score: s.orange_score,
          normalized_domain: s.normalized_domain,
          submitted_url: s.submitted_url,
          status: s.status || "complete",
          result: {
            hero: { subhead: s.hero_subhead, status: s.hero_status,
                    weakest_categories: s.hero_weakest || [] },
            categories: s.categories || {},
            top_findings: s.top_findings || [],
            complexity: s.complexity || undefined,
          },
        });
        setPrefill(r.data.prefill || null);
        if (s.submitted_url) setUrl(s.submitted_url);
      })
      .catch(() => {/* silent fallback */});
    return () => { alive = false; };
  }, [continueToken, returnView]);

  async function submit(e) {
    e?.preventDefault();
    if (!url.trim()) return;
    setErr(""); setResult(null); setBusy(true);
    try {
      const payload = { url: url.trim() };
      if (pfuToken) payload.pfu_token = pfuToken;
      const { data } = await axios.post(`${API}/api/optimise/scan`, payload);
      setResult(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "We couldn't squeeze that one. Try again shortly.");
    } finally { setBusy(false); }
  }

  function openCheckoutModal({ plan, currency, cycle }) {
    setCheckoutState({ submittingKey: null, error: "" });
    setModal({ open: true, plan, currency, cycle, error: "" });
  }

  function parseError(e) {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) {
      // Pydantic validation errors — surface the first one clearly.
      const first = d[0];
      if (first && typeof first === "object") {
        const field = Array.isArray(first.loc) ? first.loc.slice(-1)[0] : "input";
        return `Please check the ${field} field: ${first.msg || "invalid value"}`;
      }
    }
    if (e?.response?.status === 502) return "PayPal is temporarily unreachable. Please try again in a moment.";
    if (e?.response?.status === 503) return "Checkout isn't configured in this environment yet. If you're the operator, verify PAYPAL_CLIENT_ID + PAYPAL_SECRET in the environment.";
    if (!e?.response) return "Network error reaching the checkout API. Please check your connection and try again.";
    return "Something went wrong preparing checkout. Please try again shortly.";
  }

  async function submitCheckout(form) {
    setModal((m) => ({ ...m, error: "" }));
    setCheckoutState({ submittingKey: modal.plan.plan_key, error: "" });
    try {
      const { data } = await axios.post(`${API}/api/optimise/checkout`, {
        plan_key: modal.plan.plan_key,
        currency: modal.currency,
        cycle: modal.cycle,
        scan_id: result?.id,
        ...form,
      });
      // Phase 3H — the checkout endpoint no longer exposes a raw provider
      // approve_url. It returns a /agreement/{token} review page URL that
      // the customer must complete (scroll + accept) before the provider
      // destination is issued by POST /agreements/{token}/proceed.
      if (data?.review_url) {
        window.location.assign(data.review_url);
        return;
      }
      if (data?.approve_url) {
        // Backward-compat: legacy pre-3H response shape.
        window.location.assign(data.approve_url);
        return;
      }
      setModal((m) => ({ ...m, error: "Checkout didn't return a next-step URL. Please try again shortly." }));
    } catch (e) {
      setModal((m) => ({ ...m, error: parseError(e) }));
    } finally {
      setCheckoutState({ submittingKey: null, error: "" });
    }
  }

  return (
    <div className="min-h-screen text-black" style={{ fontFamily: "'Manrope',ui-sans-serif,system-ui,-apple-system,sans-serif" }}>
      <div aria-hidden className="fixed inset-0 -z-10" style={{ background: "radial-gradient(ellipse 90% 80% at 58% 45%, #FFB347 0%, #FF8A2E 30%, #FF7A00 55%, #FF6A00 80%, #D94C00 100%)" }} />
      {/* Phase 3I.4O.4 — Optimise now uses the same shared OrangeBySite
          site shell as the homepage: FixedHeader (TopPromo + Nav) + Footer
          + WhatsAppFab. No duplicate header/footer JSX lives on this page. */}
      <FixedHeader />
      <main className="relative pt-[204px] lg:pt-[132px]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 pb-24">
        {returnView ? (
          <div className="mt-4 max-w-2xl">
            <div className="text-[11px] tracking-[0.32em] uppercase font-bold">Orange Optimise</div>
            <CheckoutReturnCard mode={checkoutMode} orderId={checkoutOrderId} />
          </div>
        ) : (
          <>
            {/* HERO */}
            <div className="mt-8 max-w-2xl">
              <div className="text-[11px] tracking-[0.32em] uppercase font-bold">Orange Optimise</div>
              <h1 className="mt-3 font-black text-5xl sm:text-6xl leading-[1] tracking-[-0.03em]">HOW HEALTHY<br />IS YOUR WEBSITE?</h1>
              <p className="mt-5 text-[16px] max-w-lg text-black/80">Enter your website and Orange will analyse its public-facing health — speed, mobile experience, assets, technical setup, security configuration and efficiency. No account, no payment.</p>
              <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="optimise-form">
                <input value={url} onChange={(e) => setUrl(e.target.value)}
                       placeholder="yourbusiness.com"
                       type="text"
                       inputMode="url"
                       autoCapitalize="off"
                       autoCorrect="off"
                       spellCheck={false}
                       autoComplete="off"
                       readOnly={false}
                       disabled={false}
                       style={{ WebkitUserSelect: "text", userSelect: "text", touchAction: "auto" }}
                       className="select-text flex-1 px-5 py-4 rounded-full bg-white border-2 border-black text-[15px] font-semibold focus:outline-none focus:ring-4 focus:ring-black/20"
                       data-testid="optimise-url-input" required />
                <button type="submit" disabled={busy || !url.trim()} data-testid="optimise-scan-btn"
                        className="px-8 py-4 rounded-full bg-black text-white font-black text-[13px] tracking-[0.2em] uppercase hover:bg-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed">
                  {busy ? "Scanning…" : "Scan my site"}
                </button>
              </form>
              {err && (
                <div className="mt-4 px-5 py-3 rounded-2xl bg-black text-white text-sm" data-testid="optimise-error">
                  🍊 We couldn't squeeze that one. {err}
                </div>
              )}
              <p className="mt-3 text-[11px] tracking-[0.16em] uppercase font-bold text-black/50"
                 data-testid="optimise-url-hint">
                No http:// needed — just enter your website.
              </p>
            </div>

            {!busy && !result && <OptimiseExplainer />}

            {busy && (
              <div className="mt-14" data-testid="optimise-progress">
                <AnalysingLabels active={busy}/>
              </div>
            )}

            {result && result.status !== "failed" && (
              <div className="mt-14 space-y-8" data-testid="optimise-result">
                <div className="bg-white border-2 border-black rounded-3xl p-8 md:p-12">
                  <OrangeScoreReveal
                    score={result.orange_score}
                    band={scoreBand(result.orange_score)}
                    statusLabel={scoreCopy(result.orange_score, result.result?.hero)}
                    subtitle={`Scanned ${result.normalized_domain}`}
                    revealKey={result.id || result.normalized_domain}
                    size="hero"
                    testId="optimise-orange-score-hero"
                  />
                  {result.result?.hero?.subhead && (
                    <p className="mt-6 max-w-3xl text-[15px] leading-relaxed text-[#4A4A4A]"
                       data-testid="optimise-hero-subhead">
                      {result.result.hero.subhead}
                    </p>
                  )}
                  {(result.result?.hero?.weakest_categories || []).length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid="optimise-hero-weakest">
                      {result.result.hero.weakest_categories.map((w) => (
                        <span key={w.key}
                              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-black tracking-[0.08em] uppercase"
                              style={{
                                background: w.band === "poor" ? "#FFECEC" : "#FFF7EE",
                                color: w.band === "poor" ? "#C1350B" : "#8A2E00",
                                border: `1px solid ${w.band === "poor" ? "#FCE1C7" : "#FCE1C7"}`,
                              }}>
                          {w.label} · {w.score}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <CategoriesGrid
                  categories={result.result?.categories || {}}
                  revealKey={result.id || result.normalized_domain}
                  testIdPrefix="optimise-cat"
                />
                <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 md:p-8">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Top priorities</div>
                      <h2 className="mt-1 font-black text-2xl tracking-[-0.01em]">What to fix first</h2>
                    </div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase text-[#8A2E00] bg-[#FFF7EE] border border-[#FCE1C7]" data-testid="optimise-fix-available-note">
                      Fix available · Orange Fix quoted separately
                    </span>
                  </div>
                  <ul className="mt-5 space-y-3" data-testid="optimise-top-findings">
                    <PrioritiesReveal
                      items={result.result?.top_findings || []}
                      revealKey={result.id || result.normalized_domain}
                      testId="optimise-top-finding"
                    />
                  </ul>
                  <p className="mt-4 text-[12px] text-black/60 leading-relaxed">
                    Orange Optimise flags these issues and re-checks them every scan. Applying the actual fix on your website is a separate, quoted piece of work — either by your developer or by <strong className="text-black">Orange Fix</strong>.
                  </p>
                </div>

                <div className="bg-white border border-[#EAEAEA] rounded-3xl p-6 md:p-8">
                  <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#FF6A00]">Orange Complexity</div>
                  <div className="mt-2 flex items-baseline gap-3">
                    <div className="font-black text-4xl tracking-[-0.02em]">{result.result?.complexity?.label}</div>
                  </div>
                  <p className="mt-3 text-sm text-black/70 max-w-xl">Based on the size and technical footprint of your website. Complexity reflects how much there is to look after — not how well your site performs.</p>
                </div>

                <RecommendedPlanSection
                  result={result}
                  onCheckout={openCheckoutModal}
                  checkoutState={checkoutState}
                />

                <div className="text-center py-6">
                  <div className="font-black text-lg tracking-[0.05em]">Not sure which one?</div>
                  <p className="mt-2 text-sm text-black/70">Talk to us — we'll help you pick the right cadence for your site.</p>
                  <a href="/#discovery" className="mt-4 inline-block px-8 py-4 rounded-full bg-black text-white font-black text-[13px] tracking-[0.2em] uppercase" data-testid="optimise-cta-contact">
                    Talk to Orange
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {modal.open && (
          <CustomerDetailsModal
            open={modal.open}
            plan={modal.plan}
            currency={modal.currency}
            cycle={modal.cycle}
            scan={result}
            availability={availability}
            prefill={prefill}
            onClose={() => setModal({ ...modal, open: false })}
            onSubmit={submitCheckout}
            submitting={!!checkoutState.submittingKey}
            error={modal.error}
          />
        )}
      </AnimatePresence>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
